/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors, ToggleSwitch, Typography } from '@zapier/design-system';
import { MEDIA_MIN_WIDTH } from '../../../constants';
import { useDateTime } from '../utils';

type Props = {
  imageUrl: string;
  imageAltText: string;
  logoUrl: string;
  logoAltText: string;
  event: string;
  showToggleSwitch: boolean;
  startTime: string;
  showHeadingContainer: boolean;
  heading: string;
};

const Styles = labelStyles('AgendaHero', {
  root: css({
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateAreas: `
    'image'
    'logo'
    'bar'
    'heading'
    `,
    margin: 'auto',
    marginTop: '-1px',
    maxWidth: '1210px',
    padding: '0 20px',

    [MEDIA_MIN_WIDTH.small]: {
      gridTemplateAreas: `
      'image logo logo logo logo'
      'bar bar bar bar bar'
      'heading heading heading heading heading'
      `,
    },

    [MEDIA_MIN_WIDTH.large]: {
      gridTemplateAreas: `
      'image logo logo'
      'bar bar bar'
      'heading heading heading'
      `,
    },
  }),
  imageContainer: css({
    border: `1px solid ${Colors.earth}`,
    boxSizing: 'border-box',
    gridArea: 'image',
    height: '84px',
    overflow: 'hidden',

    [MEDIA_MIN_WIDTH.small]: {
      height: 'auto',
    },
  }),
  image: css({
    height: '100%',
    width: '100%',
    objectFit: 'cover',
    objectPosition: '65%',
  }),
  logoContainer: css({
    border: `1px solid ${Colors.earth}`,
    boxSizing: 'border-box',
    gridArea: 'logo',
    marginTop: '-1px',
    padding: '21px 23px',

    [MEDIA_MIN_WIDTH.small]: {
      marginTop: '0px',
      marginLeft: '-1px',
    },
  }),
  logo: css({
    height: '100%',
    width: '100%',
    objectFit: 'contain',
  }),
  bar: css({
    backgroundColor: Colors.earth,
    border: `1px solid ${Colors.earth}`,
    boxSizing: 'border-box',
    gridArea: 'bar',
    padding: '18px 24px',

    [MEDIA_MIN_WIDTH.small]: {
      padding: '20px',
    },
  }),
  barContentContainer: css({
    display: 'grid',
    gridGap: '9px',

    [MEDIA_MIN_WIDTH.small]: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  }),
  contentWrapper: css({
    display: 'flex',
    alignItems: 'start',
    gap: '10px',
    flexDirection: 'column',
    [MEDIA_MIN_WIDTH.small]: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: '39px',
    },
  }),

  content: css({
    display: 'flex',
  }),
  icon: css({
    height: '17px',
    width: '17px',
    marginRight: '15px',
  }),
  text: css({
    color: Colors.neutral100,
    fontFamily: 'Chivo Mono',
    fontSize: '15px',
    fontWeight: 400,
    letterSpacing: '2.06px',
    lineHeight: '115%',
    textTransform: 'uppercase',

    [MEDIA_MIN_WIDTH.large]: {
      whiteSpace: 'nowrap',
    },
  }),
  headingContainer: css({
    border: `1px solid ${Colors.earth}`,
    display: 'flex',
    flexDirection: 'column',
    gridArea: 'heading',
    marginTop: '-1px',
    padding: '80px 14px 20px',

    [MEDIA_MIN_WIDTH.small]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      padding: '155px 10px 30px 29px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      padding: '214px 17px 33px',
    },
  }),
  heading: css({
    color: Colors.earth,
    fontFamily: Typography.heading.fontFamily,
    fontSize: '40px',
    fontWeight: 500,
    letterSpacing: '1px',
    lineHeight: '42px',
    textTransform: 'uppercase',
    alignItems: 'end',

    [MEDIA_MIN_WIDTH.small]: {
      fontSize: '77px',
      letterSpacing: '0.928px',
      // This line height allows the bottom of the heading to line up with the bottom of the timezone text
      lineHeight: '70%',
      paddingRight: '10px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '88px',
      letterSpacing: '1.056px',
    },
  }),
  timezoneNoteContainer: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    [MEDIA_MIN_WIDTH.small]: {
      gap: '10px',
      flexDirection: 'row',
    },
  }),
  timezoneNote: css({
    color: Colors.earth,
    fontFamily: 'Chivo Mono',
    fontSize: '13px',
    letterSpacing: '0.16%',
    lineHeight: '130%',

    [MEDIA_MIN_WIDTH.small]: {
      alignSelf: 'flex-end',
      fontSize: '15px',
      letterSpacing: '0.18%',
    },
  }),
  toggle: css({
    [MEDIA_MIN_WIDTH.small]: {
      alignSelf: 'flex-end',
    },
    [MEDIA_MIN_WIDTH.small]: {
      top: '20px',
      height: '100%',
      position: 'relative',
    },
    [MEDIA_MIN_WIDTH.large]: {
      top: '28px',
    },
  }),
});

export function AgendaHero({
  imageUrl,
  imageAltText,
  logoUrl,
  logoAltText,
  event,
  showHeadingContainer,
  heading,
  showToggleSwitch,
  startTime,
}: Props) {
  const {
    amPM,
    compressedTime,
    day,
    setShowLocalTime,
    showLocalTime,
    timeZone,
    timeZoneAbbreviation,
    shouldLocalize,
  } = useDateTime(startTime);

  return (
    <div css={Styles.root}>
      <div css={Styles.imageContainer}>
        <img src={imageUrl} alt={imageAltText} css={Styles.image} />
      </div>
      <div css={Styles.logoContainer}>
        <img src={logoUrl} alt={logoAltText} css={Styles.logo} />
      </div>
      <div css={Styles.bar}>
        <div css={Styles.barContentContainer}>
          <div css={Styles.content}>
            <svg
              css={Styles.icon}
              viewBox="0 0 24 24"
              fill={Colors.neutral100}
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12 2C7.53 2 3.74 4.95 2.46 9h2.13a8.058 8.058 0 014.54-4.46L6.78 11H2.05C1.46 16.89 6.1 22 12 22c5.51 0 10-4.49 10-10S17.51 2 12 2zm-.56 2.03c.57-.04.57-.04 1.09 0L15.07 11H8.9l2.54-6.97zM4.07 13h2.71l2.35 6.46c-2.7-1.05-4.69-3.5-5.06-6.46zm7.37 6.97L8.9 13h6.17l-2.54 6.97c-.51.04-.52.04-1.09 0zm3.41-.5L17.2 13h2.73c-.37 2.97-2.38 5.43-5.08 6.47zM17.2 11l-2.35-6.47A8.007 8.007 0 0119.93 11H17.2z" />
            </svg>
            <p css={Styles.text}>{event}</p>
          </div>
          <div css={Styles.contentWrapper}>
            <div css={Styles.content}>
              <svg
                css={Styles.icon}
                viewBox="0 0 24 24"
                fill={Colors.neutral100}
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17 4V2h-2v2H9V2H7v2H3v2h4v2h2V6h6v2h2V6h2v13H5V7.5H3V21h18V4h-4z" />
                <path d="M8 12.25a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5zM12 12.25a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5zM16 12.25a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5zM8 17.25a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5zM12 17.25a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5zM16 17.25a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5z" />
              </svg>
              <p css={Styles.text}>{day}</p>
            </div>
            <div css={Styles.content}>
              <svg
                css={Styles.icon}
                viewBox="0 0 24 24"
                fill={Colors.neutral100}
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 18a8 8 0 110-16 8 8 0 010 16zm-1-8.47l-3.22 2.7 1.29 1.54 3.93-3.3V7h-2v4.53z" />
              </svg>
              <p css={Styles.text}>
                {`${compressedTime}${amPM} ${timeZoneAbbreviation}`}
              </p>
            </div>
          </div>
        </div>
      </div>
      {showHeadingContainer && (
        <div css={Styles.headingContainer}>
          <h1 css={Styles.heading}>{heading}</h1>
          {showToggleSwitch && (
            <div css={Styles.timezoneNoteContainer}>
              <p css={Styles.timezoneNote}>
                {shouldLocalize
                  ? `Times in ${timeZone}`
                  : `Times in ${timeZone} (PDT)`}
              </p>
              <div css={Styles.toggle}>
                {shouldLocalize && (
                  <ToggleSwitch
                    checkedText={timeZoneAbbreviation}
                    uncheckedText={timeZoneAbbreviation}
                    onChange={() => setShowLocalTime((prevState) => !prevState)}
                    checked={showLocalTime}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
